import type { OfficeDashboardSummary } from '@felix/ui';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { officeApi } from './api.js';
import { useSession } from './session.js';

/** Pano özeti 5 dakikada bir tazelenir (zil ve pano aynı veriyi kullanır). */
const REFRESH_MS = 5 * 60_000;
/** Panoya dönüşte ve sekme yeniden görününce tazeleme; iki okuma arası en az 30 sn (ADR 0156). */
export const STALE_MS = 30_000;

interface DashboardValue {
  /**
   * `undefined`: okunuyor; `null`: okunamadı (yetki ya da bağlantı). Bana düşen işler `tasks`
   * alanındadır (ADR 0156): zil ve Active Alerts paneli tek çağrıdan beslenir; eskiden zil için
   * ayrıca okunan PMS, talep, sertifika, banka ve cari bekleyenleri bu alana girdi.
   */
  summary: OfficeDashboardSummary | null | undefined;
  refresh: () => Promise<void>;
  /** Son okuma 30 sn'den eskiyse tazeler (panoya dönüş). */
  refreshIfStale: () => void;
}

const DashboardContext = createContext<DashboardValue | null>(null);

/** Ofiste pano özeti (M6d): oturum açıkken okunur; zil sayısı ve Pano ekranı buradan. */
export function DashboardProvider({ children }: { children: ReactNode }) {
  const { stage } = useSession();
  const [summary, setSummary] = useState<OfficeDashboardSummary | null | undefined>(undefined);
  const last = useRef(0);

  const refresh = useCallback(async () => {
    last.current = Date.now();
    try {
      setSummary(await officeApi.dashboard());
    } catch {
      setSummary(null);
    }
  }, []);

  const refreshIfStale = useCallback(() => {
    if (Date.now() - last.current >= STALE_MS) void refresh();
  }, [refresh]);

  useEffect(() => {
    if (stage !== 'active') {
      setSummary(undefined);
      return;
    }
    void refresh();
    const timer = setInterval(() => void refresh(), REFRESH_MS);
    // Sekme yeniden görününce (başka sekmede karar verilmiş olabilir) tazele
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [stage, refresh, refreshIfStale]);

  const value = useMemo(
    () => ({ summary, refresh, refreshIfStale }),
    [summary, refresh, refreshIfStale],
  );
  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

export function useDashboard(): DashboardValue {
  const value = useContext(DashboardContext);
  if (!value) throw new Error('DashboardProvider eksik');
  return value;
}
